import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Paper,
  Typography,
  LinearProgress,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  PersonOutline as ProfileIcon,
  ChatBubbleOutline as ChatIcon,
  PaymentOutlined as PaymentIcon
} from '@mui/icons-material';

import { saveForecastData } from '../../utils/forecastService';
import { trackStepCompleted } from '../../utils/analyticsTracking';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';

// Import new step components
import StructuredDataInput from '../Steps/StructuredDataInput';
import ChatLogInput from '../Steps/ChatLogInput';
import ForecastReview from '../Steps/ForecastReview';

const PatternLockWizard = ({ onComplete, user, existingForecast, onRequireAuth, isAuthenticated }) => {
  const { settings: platformSettings } = usePlatformSettings();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(existingForecast || {});
  const [stepValidation, setStepValidation] = useState({});
  const [autoSaving, setAutoSaving] = useState(false);
  const [currentForecastId, setCurrentForecastId] = useState(existingForecast?.id || null);
  
  const autoSaveTimeoutRef = useRef(null);
  const isCreatingForecastRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Step configuration
  const stepConfig = [
    {
      label: 'Structured Data',
      icon: <ProfileIcon />,
      description: '5-7 data points about you'
    },
    {
      label: 'Chat Log',
      icon: <ChatIcon />,
      description: 'Upload or paste your conversation'
    },
    {
      label: 'Review & Payment',
      icon: <PaymentIcon />,
      description: 'Complete your forecast order'
    }
  ];

  // Step components mapping
  const steps = [
    StructuredDataInput,
    ChatLogInput,
    ForecastReview
  ];

  // Calculate progress
  const calculateProgress = () => {
    const stepWeights = { 0: 40, 1: 40, 2: 20 };
    let totalWeight = 0;
    let completedWeight = 0;
    
    steps.forEach((step, index) => {
      const weight = stepWeights[index] || 10;
      totalWeight += weight;
      
      switch (index) {
        case 0: // Structured Data
          if (formData.age && formData.incomeTrajectory && formData.relationshipStatus && formData.biggestExpense) {
            completedWeight += weight;
          } else if (formData.age || formData.incomeTrajectory) {
            completedWeight += weight * 0.5;
          }
          break;
          
        case 1: // Chat Log
          if (formData.chatLog && formData.chatLogWordCount >= 300 && formData.chatLogWordCount <= 3000) {
            completedWeight += weight;
          } else if (formData.chatLog && formData.chatLog.length > 0) {
            completedWeight += weight * 0.3;
          }
          break;
          
        case 2: // Review
          if (currentStep >= 2) {
            completedWeight += weight;
          }
          break;
          
        default:
          break;
      }
    });
    
    return Math.min(Math.round((completedWeight / totalWeight) * 100), 100);
  };

  // Create new forecast draft - wrapped in useCallback to prevent infinite loops
  const createNewForecast = useCallback(async () => {
    if (user && !currentForecastId && !isCreatingForecastRef.current) {
      isCreatingForecastRef.current = true;
      console.log('🆕 Creating new forecast...');
      setAutoSaving(true);
      
      try {
        const ownerUserId = existingForecast?.userId || user.uid;
        const initialData = { status: 'draft' };
        const forecastId = await saveForecastData(ownerUserId, initialData, null);
        setCurrentForecastId(forecastId);
        console.log('✅ New forecast created with ID:', forecastId);
        return forecastId;
      } catch (error) {
        console.error('❌ Error creating new forecast:', error);
        return null;
      } finally {
        setAutoSaving(false);
        isCreatingForecastRef.current = false;
      }
    }
    return currentForecastId;
  }, [user, currentForecastId, existingForecast]);

  // Auto-save forecast data - wrapped in useCallback to prevent infinite loops
  const autoSaveForecast = useCallback(async (data) => {
    if (!user) return;

    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Debounce auto-save
    autoSaveTimeoutRef.current = setTimeout(async () => {
      console.log('💾 Auto-saving forecast data...');
      setAutoSaving(true);
      
      try {
        let forecastId = currentForecastId;
        
        // Create forecast if it doesn't exist
        if (!forecastId) {
          forecastId = await createNewForecast();
          if (!forecastId) {
            console.error('Failed to create forecast for auto-save');
            return;
          }
        }

        // Save to Firestore
        await saveForecastData(user.uid, data, forecastId);
        console.log('✅ Auto-save complete');
      } catch (error) {
        console.error('❌ Auto-save error:', error);
      } finally {
        setAutoSaving(false);
      }
    }, 2000); // 2 second debounce
  }, [user, currentForecastId, createNewForecast]);

  // Update form data and trigger auto-save
  const updateFormData = useCallback((updates) => {
    setFormData(prevData => {
      const newData = { ...prevData, ...updates };
      autoSaveForecast(newData);
      return newData;
    });
  }, [autoSaveForecast]);

  // Handle step validation - wrapped in useCallback to prevent infinite loops
  const handleStepValidation = useCallback((stepIndex, isValid) => {
    setStepValidation(prev => ({ ...prev, [stepIndex]: isValid }));
  }, []);

  // Create stable validation handlers for each step using useMemo
  const validationHandlers = useMemo(() => ({
    0: (isValid) => handleStepValidation(0, isValid),
    1: (isValid) => handleStepValidation(1, isValid),
    2: (isValid) => handleStepValidation(2, isValid),
  }), [handleStepValidation]);

  // Navigate to next step
  const handleNext = async () => {
    console.log(`📍 Moving from step ${currentStep} to ${currentStep + 1}`);
    
    // Check if user is authenticated before allowing to continue
    if (!isAuthenticated) {
      console.log('❌ User not authenticated, requiring auth');
      onRequireAuth();
      return;
    }

    // Track step completion
    trackStepCompleted(currentStep + 1);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step - proceed to payment
      handleComplete();
    }
  };

  // Navigate to previous step
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  // Complete wizard and proceed to payment
  const handleComplete = async () => {
    console.log('🎉 Forecast wizard complete, proceeding to payment...');
    
    try {
      let forecastId = currentForecastId;
      
      // Ensure forecast is saved
      if (!forecastId) {
        forecastId = await createNewForecast();
      }
      
      // Save final state
      await saveForecastData(user.uid, { ...formData, status: 'ready_for_payment' }, forecastId);
      
      // Pass to payment handler with forecast ID
      onComplete({ ...formData, forecastId });
    } catch (error) {
      console.error('Error completing forecast:', error);
    }
  };

  // Render current step component
  const CurrentStepComponent = steps[currentStep];
  const isStepValid = stepValidation[currentStep] !== false;
  const progress = calculateProgress();

  return (
    <Box sx={{ width: '100%', backgroundColor: '#0a0a0a', minHeight: '100vh', pt: 4, pb: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          maxWidth: 1200, 
          margin: '0 auto', 
          padding: isMobile ? 2 : 4,
          backgroundColor: '#0a0a0a',
          border: '1px solid #262626',
          borderRadius: '12px'
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 300, 
              color: '#fafafa',
              fontFamily: 'monospace',
              letterSpacing: '-1px'
            }}
          >
            {platformSettings.platformName}
          </Typography>
          <Typography variant="body1" sx={{ color: '#737373', mb: 3 }}>
            {stepConfig[currentStep].description}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
                Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {autoSaving && (
                  <>
                    <CircularProgress size={12} sx={{ color: '#737373' }} />
                    <Typography variant="caption" sx={{ color: '#737373', fontFamily: 'monospace' }}>
                      Saving...
                    </Typography>
                  </>
                )}
                <Typography variant="body2" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
                  {progress}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#171717',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#fafafa',
                  borderRadius: 4
                }
              }} 
            />
          </Box>
        </Box>

        {/* Stepper */}
        <Stepper 
          activeStep={currentStep} 
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ 
            mb: 4,
            '& .MuiStepLabel-root .Mui-completed': { color: '#fafafa' },
            '& .MuiStepLabel-root .Mui-active': { color: '#fafafa' },
            '& .MuiStepLabel-label': { color: '#737373', fontFamily: 'monospace' },
            '& .MuiStepLabel-label.Mui-active': { color: '#fafafa' },
            '& .MuiStepLabel-label.Mui-completed': { color: '#fafafa' },
            '& .MuiStepConnector-line': { borderColor: '#262626' }
          }}
        >
          {stepConfig.map((step, index) => (
            <Step key={step.label}>
              <StepLabel icon={step.icon}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: '400px' }}>
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={validationHandlers[currentStep]}
            user={user}
          />
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid #262626' }}>
          <Button
            disabled={currentStep === 0}
            onClick={handleBack}
            sx={{
              color: '#737373',
              fontFamily: 'monospace',
              '&:hover': { backgroundColor: '#171717' },
              '&.Mui-disabled': { color: '#404040' }
            }}
          >
            Back
          </Button>
          
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isStepValid}
            sx={{
              backgroundColor: '#fafafa',
              color: '#0a0a0a',
              fontFamily: 'monospace',
              fontWeight: 600,
              px: 4,
              '&:hover': { backgroundColor: '#e5e5e5' },
              '&.Mui-disabled': { backgroundColor: '#262626', color: '#525252' }
            }}
          >
            {currentStep === steps.length - 1 ? 'Complete Purchase' : 'Continue'}
          </Button>
        </Box>

        {/* Step Info */}
        {!isStepValid && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#ef4444', fontFamily: 'monospace' }}>
              Please complete all required fields to continue
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PatternLockWizard;

